/**
 * VideoGenerationModal.js
 * 
 * Modal for generating video from existing content.
 * Takes visual_direction and transforms it into a video prompt
 * with camera movement and duration options.
 */

import React, { useState, useEffect } from 'react';
import { FiX, FiVideo, FiCamera, FiClock, FiEdit3, FiZap } from 'react-icons/fi';
import { api, getFullApiUrl } from '../services/api';

// Camera movement options with descriptions
const CAMERA_MOVEMENTS = [
  { id: 'static', name: 'Static', icon: '📷', description: 'Subtle atmospheric movement only' },
  { id: 'dolly_in', name: 'Push In', icon: '🔍', description: 'Slowly zoom towards subject' },
  { id: 'dolly_out', name: 'Pull Out', icon: '🔭', description: 'Slowly zoom away, reveal context' },
  { id: 'dolly_left', name: 'Dolly Left', icon: '⬅️', description: 'Smooth pan to the left' },
  { id: 'dolly_right', name: 'Dolly Right', icon: '➡️', description: 'Smooth pan to the right' },
  { id: 'orbit', name: 'Orbit', icon: '🔄', description: 'Rotate around the subject' },
  { id: 'jib_up', name: 'Crane Up', icon: '⬆️', description: 'Rise upward, reveal from above' },
  { id: 'jib_down', name: 'Crane Down', icon: '⬇️', description: 'Lower down, intimate reveal' },
];

// Duration options
const DURATION_OPTIONS = [
  { value: 3, label: '3 sec', description: 'Quick clip' },
  { value: 5, label: '5 sec', description: 'Standard' },
  { value: 8, label: '8 sec', description: 'Cinematic' },
];

export default function VideoGenerationModal({ content, onClose, onGenerate }) {
  const [cameraMovement, setCameraMovement] = useState('dolly_in');
  const [duration, setDuration] = useState(5);
  const [videoPrompt, setVideoPrompt] = useState('');
  const [isGeneratingPrompt, setIsGeneratingPrompt] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [error, setError] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);
  const [loadingImage, setLoadingImage] = useState(true);

  // Load image on mount
  useEffect(() => {
    const loadImage = async () => {
      try {
        const response = await api.getContentImage(content.id);
        if (response?.image_url) {
          setImageUrl(getFullApiUrl(response.image_url));
        }
      } catch (err) {
        console.error('Failed to load image:', err);
      } finally {
        setLoadingImage(false);
      }
    };
    loadImage();
  }, [content.id]);

  // Generate video prompt when camera movement or duration changes
  useEffect(() => {
    generateVideoPrompt();
  }, [cameraMovement, duration]);

  const generateVideoPrompt = async () => {
    if (!content.visual_direction) {
      setError('This content has no visual direction. Cannot generate video prompt.');
      return;
    }

    setIsGeneratingPrompt(true);
    setError(null);

    try {
      const response = await api.generateVideoPrompt({
        content_id: content.id,
        visual_direction: content.visual_direction,
        camera_movement: cameraMovement,
        duration: duration,
        hook: content.hook,
        caption: content.caption,
      });

      if (response?.video_prompt) {
        setVideoPrompt(response.video_prompt);
      } else {
        throw new Error('No video prompt returned');
      }
    } catch (err) {
      console.error('Failed to generate video prompt:', err);
      setError('Failed to generate video prompt. Please try again.');
      // Fallback to basic template if API fails
      setVideoPrompt(createFallbackPrompt(content.visual_direction, cameraMovement, duration));
    } finally {
      setIsGeneratingPrompt(false);
    }
  };

  // Fallback template-based prompt if API fails
  const createFallbackPrompt = (visualDirection, camera, dur) => {
    const movementIntros = {
      static: 'Static shot with subtle atmospheric movement.',
      dolly_in: 'Slow push-in shot drawing closer to the subject.',
      dolly_out: 'Gradual pull-out shot revealing the wider scene.',
      dolly_left: 'Smooth dolly shot panning left across the scene.',
      dolly_right: 'Smooth dolly shot panning right across the scene.',
      orbit: 'Cinematic orbit shot rotating around the subject.',
      jib_up: 'Crane shot rising upward to reveal the scene from above.',
      jib_down: 'Crane shot lowering down for an intimate perspective.',
    };

    return `${movementIntros[camera]} ${visualDirection} Subtle dust particles float in the light, shadows shift gently. ${dur} second shot.`;
  };

  const handleGenerateVideo = () => {
    if (!videoPrompt) return;
    
    // For now, just show what would be sent
    onGenerate?.({
      content_id: content.id,
      video_prompt: videoPrompt,
      camera_movement: cameraMovement,
      duration: duration,
      source_image_url: imageUrl,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
      <div className="bg-card border border-border rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-purple-500/20 flex items-center justify-center">
              <FiVideo className="text-purple-400" size={20} />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-foreground">Create Video</h2>
              <p className="text-sm text-muted-foreground">Transform your content into video</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-muted transition-colors"
            data-testid="close-video-modal"
          >
            <FiX size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          {/* Source Content Preview */}
          <div className="flex gap-4">
            <div className="w-32 h-32 rounded-xl overflow-hidden bg-muted/20 flex-shrink-0">
              {loadingImage ? (
                <div className="w-full h-full flex items-center justify-center">
                  <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                </div>
              ) : imageUrl ? (
                <img src={imageUrl} alt="Source" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                  <FiCamera size={24} />
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm text-foreground line-clamp-2 font-medium">
                {content.hook || 'Untitled content'}
              </p>
              <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                {content.caption?.substring(0, 100)}...
              </p>
              {content.overlay_primary && (
                <div className="mt-2 inline-flex items-center px-2 py-1 rounded bg-purple-500/20 text-purple-300 text-xs">
                  {content.overlay_primary}
                </div>
              )}
            </div>
          </div>

          {/* Camera Movement Selection */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-3">
              <FiCamera className="inline mr-2" size={16} />
              Camera Movement
            </label>
            <div className="grid grid-cols-4 gap-2">
              {CAMERA_MOVEMENTS.map((movement) => (
                <button
                  key={movement.id}
                  onClick={() => setCameraMovement(movement.id)}
                  className={`p-3 rounded-xl border-2 text-center transition-all ${
                    cameraMovement === movement.id
                      ? 'border-purple-500 bg-purple-500/10 ring-2 ring-purple-500/30'
                      : 'border-border hover:border-purple-500/50'
                  }`}
                  data-testid={`camera-${movement.id}`}
                >
                  <div className="text-2xl mb-1">{movement.icon}</div>
                  <div className="text-xs font-medium text-foreground">{movement.name}</div>
                </button>
              ))}
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              {CAMERA_MOVEMENTS.find(m => m.id === cameraMovement)?.description}
            </p>
          </div>

          {/* Duration Selection */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-3">
              <FiClock className="inline mr-2" size={16} />
              Duration
            </label>
            <div className="flex gap-3">
              {DURATION_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => setDuration(opt.value)}
                  className={`flex-1 p-3 rounded-xl border-2 text-center transition-all ${
                    duration === opt.value
                      ? 'border-purple-500 bg-purple-500/10 ring-2 ring-purple-500/30'
                      : 'border-border hover:border-purple-500/50'
                  }`}
                  data-testid={`duration-${opt.value}`}
                >
                  <div className="text-lg font-bold text-foreground">{opt.label}</div>
                  <div className="text-xs text-muted-foreground">{opt.description}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Video Prompt Preview */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="text-sm font-medium text-foreground">
                <FiZap className="inline mr-2" size={16} />
                Video Prompt
                <span className="text-xs text-muted-foreground ml-2">(AI-generated)</span>
              </label>
              <button
                onClick={() => setIsEditing(!isEditing)}
                className="flex items-center gap-1 text-xs text-purple-400 hover:text-purple-300"
                data-testid="toggle-edit-prompt"
              >
                <FiEdit3 size={12} />
                {isEditing ? 'Done' : 'Edit'}
              </button>
            </div>
            
            {isGeneratingPrompt ? (
              <div className="p-4 rounded-xl bg-muted/20 border border-border flex items-center justify-center gap-3">
                <div className="w-5 h-5 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
                <span className="text-sm text-muted-foreground">Generating video prompt...</span>
              </div>
            ) : isEditing ? (
              <textarea
                value={videoPrompt}
                onChange={(e) => setVideoPrompt(e.target.value)}
                className="w-full p-4 rounded-xl bg-muted/20 border border-purple-500/50 text-foreground text-sm resize-none focus:outline-none focus:ring-2 focus:ring-purple-500/50"
                rows={5}
                data-testid="video-prompt-textarea"
              />
            ) : (
              <div className="p-4 rounded-xl bg-muted/20 border border-border">
                <p className="text-sm text-foreground leading-relaxed">
                  {videoPrompt || 'Prompt will appear here...'}
                </p>
              </div>
            )}

            {error && (
              <p className="text-xs text-red-400 mt-2">{error}</p>
            )}

            <p className="text-xs text-muted-foreground mt-2">
              💡 This prompt combines your content's visual direction with the selected camera movement.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-border bg-muted/20">
          <div className="flex items-center justify-between">
            <div className="text-sm text-muted-foreground">
              <span className="text-purple-400 font-medium">3 credits</span> per video
            </div>
            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="px-4 py-2 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                data-testid="cancel-video"
              >
                Cancel
              </button>
              <button
                onClick={handleGenerateVideo}
                disabled={!videoPrompt || isGeneratingPrompt}
                className="px-6 py-2 rounded-lg text-sm font-medium bg-purple-600 text-white hover:bg-purple-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                data-testid="generate-video-btn"
              >
                <FiVideo size={16} />
                Generate Video
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
